import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchApplyNowTracking } from 'externals/_services/tracking.service';
import {
  plantMetadataInLocalStorageAndReturnTransferUrl,
  convertProps,
  setPartnerNameAndImageWithId,
} from 'partial/OfferManager/holder/genericOfferLogic';
import { withRouter } from 'react-router-dom';
import {
  CLICK_APPLY,
  CLICK,
  SEEN_OFFER,
} from 'externals/_tracking/types/eventTypes.constants';
import { cleanPopupPaths } from 'helpers/path.helper';
import { getFullAttributes } from 'helpers/tracking.helpers';
import { useLogger } from 'hook/error.hook';
import { useDispatch } from 'react-redux';
import { selectPersonalLoanForPrequalification } from 'actions/product.actions';
import VisibilitySensor from 'react-visibility-sensor';
import useTrackingMetadata from 'externals/_tracking/useTrackingMetadata';
import { useConfig } from 'hook/config.hook';
import { wrapService } from 'actions/service_wrapper.actions';
import { getMrphPageFormFactor } from 'helpers/metadataLogic.helpers';

const a_fetchApplyNowTracking = wrapService(fetchApplyNowTracking, {
  name: 'pl_fetchApplyNowTracking',
});

const PersonalLoanOfferHolder = props => {
  const trackingManager = useTrackingMetadata();
  const { pageNameMeta, setPagePosition } = trackingManager;
  const isModal = props.isModal;
  const logger = useLogger({
    name: 'PersonalLoanOfferHolder',
  });
  const dispatch = useDispatch();
  const { config } = useConfig();
  const [isSeenTriggered, setIsSeenTriggered] = useState(false);
  const { ocfLoggedIn } = useSelector(state => ({
    ocfLoggedIn: state.app.ocfLoggedIn,
  }));
  const [isLoading, setIsLoading] = useState(false);

  const defaultEventHandler = (name, attributes = {}) => {
    const fullAttributes = getFullAttributes({
      partner: props.partner,
      pagePosition: props.pagePosition,
      modulePosition: props.modulePosition,
      offerMetadata: props.offerMetadata,
      position: props.position,
      extraAttributes: props.extraAttributes,
      attributes: attributes,
      isModal: isModal,
    });
    props.eventHandler(name, fullAttributes);
  };

  const onApplyClick = (e, attr) => {
    if (isLoading) return;
    setIsLoading(true);
    e.preventDefault();
    const id = setPartnerNameAndImageWithId(
      props.offerMetadata.name,
      props.offerMetadata.imageLink,
    );
    const propsForBackend = convertProps(
      getFullAttributes({
        partner: props.partner,
        pagePosition: props.pagePosition,
        modulePosition: props.modulePosition,
        offerMetadata: props.offerMetadata,
        position: props.position,
        extraAttributes: props.extraAttributes,
        attributes: {
          'Click Type': 'Apply',
          'Page Name': pageNameMeta,
          'Page Form Factor': getMrphPageFormFactor(),
        },
        isModal: isModal,
        includeBackendRefCode: true,
      }),
    );

    let windowReference = window.open();

    dispatch(
      a_fetchApplyNowTracking(props.offerMetadata.linkUrl, {
        props: propsForBackend,
      }),
    )
      .then(trackingMetadata => {
        defaultEventHandler(attr?.event ?? CLICK_APPLY, {
          'Click Type': attr?.clickType ?? 'Apply',
          'Click ID': trackingMetadata.clickId,
          'Page Form Factor': getMrphPageFormFactor(),
        });

        const transferUrl = plantMetadataInLocalStorageAndReturnTransferUrl(
          trackingMetadata.partnerUrl,
          id,
          null,
          ocfLoggedIn,
          props.isPublicUser,
        );

        windowReference.location = transferUrl;
        setIsLoading(false);
      })
      .catch(err => {
        logger.showError('A problem happened while Transferring', err.message);
        logger.reportAPIError(err);
        windowReference.close();
        setIsLoading(false);
      });
  };

  const onMouseOverApprovalOdds = e => {
    defaultEventHandler(CLICK, {
      'Click Type': 'ApprovalOdds',
    });
  };

  const onMouseClickReview = e => {
    e.preventDefault();
    defaultEventHandler(CLICK, {
      'Click Type': 'User Reviews',
    });

    openDetailsTab('reviews');
  };

  const onMouseClickDetails = e => {
    const clickType = props.offerMetadata.isPrequalOffer
      ? 'Details and rate disclosure'
      : 'Loan Details';
    defaultEventHandler(CLICK, {
      'Click Type': clickType,
    });

    openDetailsTab();
  };

  const onMouseClickDisclaimer = e => {
    defaultEventHandler(CLICK, {
      'Click Type': 'Loan Details',
    });
    openDetailsTab('disclaimerContent');
  };

  const openDetailsTab = type => {
    const currentPath = props.location.pathname;
    const cleanPath = cleanPopupPaths(currentPath);
    const hashTag = type ? `#${type}` : '';
    const currentLocationSearch = props?.location?.search
      ? props?.location?.search
      : '';

    const finalUrl = `${cleanPath}/offerDetails/${
      props.offerMetadata.isPrequalOffer
        ? 'personalLoanPrequal'
        : 'personalLoan'
    }/${props.offerMetadata.personalLoanId}${currentLocationSearch}${hashTag}`;
    setPagePosition(props.pagePosition);
    props.history.replace(finalUrl);
  };

  const clickPrequalHandler = e => {
    defaultEventHandler(CLICK, {
      'Click Type': 'Prequal',
    });

    if (ocfLoggedIn) {
      props.history.push('/offers?vertical=PERSONAL_LOAN');
      return;
    }

    const currentPath = props.location.pathname;

    dispatch(selectPersonalLoanForPrequalification(props.offerMetadata));

    const cleanPath = cleanPopupPaths(currentPath);
    const finalUrl = `${cleanPath}/prequalFlow/${props.offerMetadata.providerId}`;
    setPagePosition(props.pagePosition);
    props.history.replace(finalUrl);
  };

  const onVisibilityChange = isVisible => {
    if (isVisible === true && !isSeenTriggered) {
      const attrs = {};
      defaultEventHandler(SEEN_OFFER, attrs);
      setIsSeenTriggered(true);
    }
  };

  return (
    <props.personalLoanTemplate
      {...props}
      offerImageLinkUrlProxy={`${props.offerMetadata?.imageLink}`}
      clickApplyImageHandler={onApplyClick}
      clickApplyHandler={onApplyClick}
      clickDisclaimerHandler={onMouseClickDisclaimer}
      clickReviewHandler={onMouseClickReview}
      clickDetailsHandler={onMouseClickDetails}
      clickPrequalHandler={clickPrequalHandler}
      hoverApprovalOddsHandler={onMouseOverApprovalOdds}
      config={config}
      seenPixel={
        <VisibilitySensor onChange={onVisibilityChange}>
          <div className="invisible-pixel" />
        </VisibilitySensor>
      }
    />
  );
};

PersonalLoanOfferHolder.defaultProps = {
  extraEventAttributes: {},
};

const connectedPersonalLoanOfferHolder = withRouter(PersonalLoanOfferHolder);

export default connectedPersonalLoanOfferHolder;
